import { SystemStyleObject } from '@chakra-ui/react'

import { tokenVar } from '../../theme/tokenVar'
import type { ColorScheme } from '../../types'

type MediaCarouselTokens = {
  border: string
  skeletonFill: string
  arrowIdle: string
  arrowHover: string
  dotActive: string
  dotInactive: string
  overlayToken: string
}

const tokens: Record<ColorScheme, MediaCarouselTokens> = {
  dark: {
    border: 'border.subtle',
    skeletonFill: 'bg.skeleton',
    arrowIdle: 'icon.secondary',
    arrowHover: 'icon.primary',
    dotActive: 'icon.primary',
    dotInactive: 'icon.secondary',
    overlayToken: 'colors.bg.overlay',
  },
  light: {
    border: 'light.border.subtle',
    skeletonFill: 'light.bg.skeleton',
    arrowIdle: 'light.icon.secondary',
    // The hovered arrow sits on the dark scrim gradient, so white
    // (light.icon.primary) reads well there. The active dot sits on the bare
    // light surface, where white vanishes — it emphasizes toward
    // light.text.primary instead (the trap LocationBreadcrumb's copy button
    // documents).
    arrowHover: 'light.icon.primary',
    dotActive: 'light.text.primary',
    dotInactive: 'light.icon.secondary',
    overlayToken: 'colors.light.bg.overlay',
  },
}

export const mediaCarouselFrameStyles = (
  colorScheme: ColorScheme,
  isLoading: boolean,
  width: string
): SystemStyleObject => ({
  // Loading fill only — a resting bg would letterbox the media.
  bg: isLoading ? tokens[colorScheme].skeletonFill : undefined,
  border: '1px solid',
  borderColor: tokens[colorScheme].border,
  borderRadius: '0',
  h: '100%',
  maxW: '100%',
  w: width,
})

export const mediaCarouselColumnStyles: SystemStyleObject = {
  display: 'flex',
  flexDirection: 'column',
}

export const mediaCarouselMediaBoxStyles: SystemStyleObject = {
  position: 'relative',
}

/**
 * Full-height strip over the media's side margin — clicking anywhere in the
 * margin navigates, not just the chevron. Hover brightens the chevron
 * (currentColor) and paints an overlay scrim fading over the media.
 */
export const mediaCarouselNavZoneStyles = (
  colorScheme: ColorScheme,
  side: 'left' | 'right'
): SystemStyleObject => ({
  alignItems: 'center',
  bg: 'transparent',
  border: 'none',
  color: tokens[colorScheme].arrowIdle,
  cursor: 'pointer',
  display: 'flex',
  h: '100%',
  justifyContent: side === 'left' ? 'flex-start' : 'flex-end',
  p: 0,
  position: 'absolute',
  top: 0,
  // The 48px glyph plus a little slack — a wide zone would eat clicks meant
  // for the media itself.
  w: '64px',
  zIndex: 1,
  ...(side === 'left' ? { left: 0 } : { right: 0 }),
  _hover: {
    backgroundImage: `linear-gradient(${
      side === 'left' ? 'to right' : 'to left'
    }, ${tokenVar(tokens[colorScheme].overlayToken)}, transparent)`,
    color: tokens[colorScheme].arrowHover,
  },
  _focusVisible: {
    outline: '2px solid',
    outlineColor:
      colorScheme === 'light'
        ? 'light.interactive.focus-ring'
        : 'interactive.focus-ring',
    outlineOffset: '-2px',
  },
})

export const mediaCarouselDotsRowStyles: SystemStyleObject = {
  alignItems: 'center',
  display: 'flex',
  gap: '8px',
  justifyContent: 'center',
  py: '8px',
  w: '100%',
}

export const mediaCarouselDotStyles = (
  colorScheme: ColorScheme,
  isActive: boolean
): SystemStyleObject => ({
  borderRadius: 'full',
  flexShrink: 0,
  h: '12px',
  w: '12px',
  ...(isActive
    ? { bg: tokens[colorScheme].dotActive }
    : {
        border: '1.5px solid',
        borderColor: tokens[colorScheme].dotInactive,
      }),
})
